import React, { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import {
  ETierKey,
  ITier,
  tiers,
} from '@/pages/subscribe/_data/subscribe-constant'
import TrialPlansComparisonModal from './trial-plans-comparison-modal'
import TrialContactModal from './trial-contact-modal'
import TrialSubscribeModal from './trial-subscribe-modal'
import TrialPaymentSuccess from './trial-payment-success'
import { useQueryState } from 'nuqs'

type BillingCycle = 'monthly' | 'annual'

interface Props {
  isOpen: boolean
  isDiscount?: boolean
  onOpenChange: (isOpen: boolean) => void
}

export const TRIAL_PLANS_QUERY_KEY = 'trial-plans'

export default function TrialPlansModal({
  isOpen,
  isDiscount,
  onOpenChange,
}: Props) {
  const [_, setTrialPlansState] = useQueryState(TRIAL_PLANS_QUERY_KEY)

  const removeStylesheet = (href: string) => {
    const link = document.querySelector(`link[href="${href}"]`)
    if (link) {
      document.body.removeChild(link)
    }
  }
  removeStylesheet('/css/landing.css')
  removeStylesheet('/css/bootstrap.css')

  const [isOpenComparisonModal, setIsOpenComparisonModal] = useState(false)
  const [isOpenContactModal, setIsOpenContactModal] = useState(false)
  const [isOpenPaymentSuccess, setIsOpenPaymentSuccess] = useState(false)
  const [tierSelected, setTierSelected] = useState<
    | {
        tierKey: ETierKey
        tier: ITier
      }
    | undefined
  >(undefined)
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')

  const handlePlanSelection = (tierKey: ETierKey, tier: ITier) => {
    if (tierKey === ETierKey.Enterprise) {
      setIsOpenContactModal(true)
      onOpenChange(false)
      return
    }
    setTierSelected({
      tierKey: tierKey,
      tier: tier,
    })
    onOpenChange(false)
  }

  const tierKeys = [
    ETierKey.Advanced,
    ETierKey.Professional,
    ETierKey.Enterprise,
  ]

  useEffect(() => {
    if (tierSelected) return
    if (isOpenComparisonModal) return
    if (isOpen) return
    setBillingCycle('monthly')
  }, [isOpen, tierSelected, isOpenContactModal])

  useEffect(() => {
    if (isOpen) {
      setTrialPlansState('open')
    } else {
      setTrialPlansState(null)
    }
  }, [isOpen])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          showClose={true}
          className="!p-8 rounded-lg max-w-7xl md:gap-6"
        >
          <DialogHeader>
            <DialogTitle className="md:text-[32px]">Choose a plan</DialogTitle>
          </DialogHeader>

          <Tabs
            autoFocus={false}
            defaultValue="monthly"
            value={billingCycle}
            onValueChange={(value) =>
              setBillingCycle(value as 'monthly' | 'annual')
            }
          >
            <TabsList className="w-full">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 w-full">
                <div className="flex flex-row items-center justify-start w-fit rounded-full ">
                  <TabsTrigger asChild value="monthly">
                    <div className="max-w-[172px] w-full h-8 px-[58px] border border-[#ABB2B5] border-r-0 data-[state=active]:border-r-[1.5px] rounded-l-full cursor-pointer data-[state=active]:border-primary data-[state=active]:text-primary">
                      {`Monthly`}
                    </div>
                  </TabsTrigger>
                  <TabsTrigger asChild value="annual">
                    <div className="max-w-[172px] w-full h-8 px-[58px] border border-[#ABB2B5] border-l-0 rounded-r-full cursor-pointer data-[state=active]:border-l-[1.5px] data-[state=active]:border-primary data-[state=active]:text-primary">
                      {`Annual (Save 20%)`}
                    </div>
                  </TabsTrigger>
                </div>
                <div className="flex justify-end w-full text-sm cursor-pointer">
                  <div
                    onClick={() => {
                      onOpenChange(false)
                      return setIsOpenComparisonModal(true)
                    }}
                    className="flex flex-row gap-1 items-center text-primary hover:text-blue-800"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.99967 9.3335H1.33301V10.6668H5.99967V12.6668L8.66634 10.0002L5.99967 7.3335V9.3335ZM9.99967 8.66683V6.66683H14.6663V5.3335H9.99967V3.3335L7.33301 6.00016L9.99967 8.66683Z"
                        fill="currentColor"
                      />
                    </svg>

                    <p>View and Compare All Features</p>
                  </div>
                </div>
              </div>
            </TabsList>
            <TabsContent className="mt-[30px]" value="monthly">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tierKeys.map((tierKey) => (
                  <div
                    key={tierKey}
                    className="border-[0.5px] border-[#B9B9B9] rounded-lg p-8 flex flex-col h-full hover:shadow-lg transition-shadow duration-200"
                  >
                    <TierCard
                      tierKey={tierKey}
                      isAnnual={false}
                      isDiscount={isDiscount}
                      handlePlanSelection={handlePlanSelection}
                    />
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent className="mt-[30px]" value="annual">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tierKeys.map((tierKey) => (
                  <div
                    key={tierKey}
                    className="border-[0.5px] border-[#B9B9B9] rounded-lg p-8 flex flex-col h-full hover:shadow-lg transition-shadow duration-200"
                  >
                    <TierCard
                      tierKey={tierKey}
                      isAnnual={true}
                      isDiscount={isDiscount}
                      handlePlanSelection={handlePlanSelection}
                    />
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      <TrialPlansComparisonModal
        open={isOpenComparisonModal}
        onClose={() => {
          setIsOpenComparisonModal(false)
          onOpenChange(true)
        }}
      />
      <TrialContactModal
        open={isOpenContactModal}
        onSubmittedClose={() => {
          setIsOpenContactModal(false)
          onOpenChange(false)
        }}
        onClose={() => {
          setIsOpenContactModal(false)
          onOpenChange(true)
        }}
      />
      <TrialSubscribeModal
        tier={tierSelected?.tier}
        tierKey={tierSelected?.tierKey}
        isAnnual={billingCycle === 'annual'}
        isDiscount={isDiscount}
        onClose={() => {
          setTierSelected(undefined)
          onOpenChange(true)
        }}
        onPaymentSuccess={() => {
          setTierSelected(undefined)
          setIsOpenPaymentSuccess(true)
        }}
      />
      <TrialPaymentSuccess
        isOpen={isOpenPaymentSuccess}
        onOpenChange={setIsOpenPaymentSuccess}
      />
    </>
  )
}

export const TierCard = ({
  tierKey,
  isSubscribe,
  isAnnual,
  isDiscount,
  handlePlanSelection,
}: {
  tierKey: ETierKey
  isSubscribe?: boolean
  isAnnual: boolean
  isDiscount?: boolean
  handlePlanSelection?: (tierKey: ETierKey, tier: ITier) => void
}) => {
  let tier = tiers[tierKey]

  if (isAnnual) {
    if (tierKey === ETierKey.Advanced) {
      tier = tiers[ETierKey.AdvancedAnnual]
    } else if (tierKey === ETierKey.Professional) {
      tier = tiers[ETierKey.ProfessionalAnnual]
    }
  }

  const isEnterprise = tierKey === ETierKey.Enterprise
  const priceOriginal = isAnnual && isSubscribe ? tier.price_annual : tier.price
  const priceDiscount =
    isSubscribe && isAnnual
      ? isDiscount
        ? tier.price_annual_discount
        : tier.price_annual
      : isDiscount
        ? tier.price_discount
        : tier.price

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-2 h-full max-w-[356px] w-full ">
        <h2 className="font-bold text-2xl text-charcoal">{tier.name}</h2>
        <div className="flex flex-col gap-1 mb-2">
          {/* Show original price if discount is applied */}
          {isDiscount && !isEnterprise && (
            <p className="text-sm text-destructive line-through">
              {priceOriginal}
            </p>
          )}
          <p className="text-lg font-semibold text-charcoal">
            {priceDiscount}
            {isAnnual && !isEnterprise && ', billed annually'}
          </p>

          {/* Show discount banner if discount is applied */}
          {isDiscount && !isEnterprise && (
            <div className="w-full py-1 px-2 items-center bg-charcoal rounded-sm flex justify-center">
              <p className="text-sm  text-center text-white font-semibold">
                INCLUDING ONE-TIME 20% DISCOUNT
              </p>
              t
            </div>
          )}
        </div>
        <p className="font-bold text-base text-charcoal mb-2">{tier.title}</p>

        <div className="flex flex-col gap-4">
          {tier.desc.map((feature: string, index: number) => (
            <div key={index} className="flex items-center w-full">
              <img
                src="/icons/check-icon.svg"
                alt="check"
                className="w-4 h-4 mr-4 text-primary"
              />
              <span className="text-charcoal">{feature}</span>
            </div>
          ))}
        </div>
      </div>
      {handlePlanSelection && (
        <Button
          className={`mt-8 w-full ${
            isEnterprise ? 'bg-primary hover:bg-[#01589d]' : ''
          }`}
          onClick={() => {
            return handlePlanSelection(tierKey, tier)
          }}
        >
          {isEnterprise ? 'Contact Sales' : 'Choose Plan'}
        </Button>
      )}
    </div>
  )
}
